import React from 'react';
import { marked } from 'marked';
import clsx from 'clsx';
import { Grid, Tabs } from 'antd';
import { MarkdownContent } from 'components/common/MarkdownContent';

interface MarkdownTabsProps {
    content: string;
    className?: string;
}

interface TabSection {
    title: string;
    body: string;
    showTitle: boolean;
}

// Tabbed variant of MarkdownContent, matching the legacy vendor instructions viewer
// (Legacy markdown-viewer.js, data-tabs="h1"): the rendered content is split on top-level
// H1s and each H1 plus everything up to the next H1 becomes a tab. Content with no H1s
// falls back to plain MarkdownContent, so authors opt in simply by using H1 headings.
export const MarkdownTabs: React.FC<MarkdownTabsProps> = ({ content, className }) => {

    const screens = Grid.useBreakpoint();

    const sections = React.useMemo<TabSection[]>(() => {
        const html = marked.parse(content || '', { async: false }) as string;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const h1s = Array.from(doc.body.querySelectorAll('h1'));
        if (h1s.length === 0) {
            return [];
        }

        const result: TabSection[] = [];

        // Content before the first H1 — older shows often use a lone bold line as the first
        // "heading" instead of an H1. Keep it as a tab of its own (labelled from its first
        // element) rather than silently dropping it like the legacy viewer did.
        const leading: string[] = [];
        let node = doc.body.firstElementChild;
        while (node && node.tagName !== 'H1') {
            leading.push(node.outerHTML);
            node = node.nextElementSibling;
        }
        if (leading.length > 0) {
            const label = (doc.body.firstElementChild?.textContent || '').trim();
            result.push({
                title: label.length > 40 ? `${label.slice(0, 40)}…` : label || 'General',
                body: leading.join(''),
                // The body already opens with the line the label came from — no separate title.
                showTitle: false,
            });
        }

        h1s.forEach(h1 => {
            const parts: string[] = [];
            let sibling = h1.nextElementSibling;
            while (sibling && sibling.tagName !== 'H1') {
                parts.push(sibling.outerHTML);
                sibling = sibling.nextElementSibling;
            }
            // The body is already-rendered HTML; MarkdownContent passes raw HTML through
            // unchanged and applies the shared link/table/image treatment.
            result.push({ title: h1.textContent || '', body: parts.join(''), showTitle: true });
        });

        return result;
    }, [content]);

    if (sections.length === 0) {
        return <MarkdownContent className={className} content={content} />;
    }

    return (
        // The wrapper takes the caller's className (content-html on the instructions page),
        // so the whole tab block gets the page's centered max-width column.
        <div className={clsx('markdown-tabs', className)}>
            <Tabs
                tabPlacement={screens.md ? 'start' : 'top'}
                items={sections.map((section, index) => ({
                    key: String(index),
                    label: section.title,
                    children: (
                        <div>
                            {/* h2, not h1 — .page-content h1 is the fixed page-title banner */}
                            {section.showTitle && <h2 className="markdown-tab-title">{section.title}</h2>}
                            <MarkdownContent content={section.body} />
                        </div>
                    ),
                }))}
            />
        </div>
    );
};
